import React, { useState, useEffect } from 'react';
import {
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Button, TablePagination, CircularProgress, styled, MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import { makeApiRequest } from '../../../utils/api';
import { toast, ToastContainer } from 'react-toastify';
import Autocomplete from '@mui/material/Autocomplete';
import 'react-toastify/dist/ReactToastify.css';
import { formatDatewithMonth } from '../../../helpers/handleDate';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ViewLead from './ViewLead';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: 500,
  color: 'black',
  borderBottom: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
}));

const columnWidths = {
  sno: '100px',
  name: '280px',
  email: '250px',
  phone: '140px',
  date: '170px',
  courseDepartment: '400px',
  year: '180px',
  createdByDate: '200px',
};

const StyledTableHeaderCell = styled(TableCell)(({ theme, width }) => ({
  padding: theme.spacing(2, 3),
  textAlign: 'left',
  fontSize: '14px',
  fontWeight: 'bold',
  fontFamily: 'Roboto',
  backgroundColor: '#f5f5f5',
  color: 'black',
  borderBottom: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
  width: width,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  boxSizing: 'border-box',
}));

const StyledTableRow = styled(TableRow)(() => ({}));

const TabButton = styled(Button)(({ active }) => ({
  color: active ? '#1976d2' : '#757575',
  backgroundColor: 'transparent',
  fontSize: '14px',
  fontWeight: 500,
  textTransform: 'none',
  padding: '8px 12px',
  minWidth: 'auto',
  borderBottom: active ? '2px solid #1976d2' : 'none',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
    borderBottom: active ? '2px solid #1976d2' : 'none',
  },
}));

const FollowUpLead = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('New');
  const [searchTerm, setSearchTerm] = useState('');
  const [leadsData, setLeadsData] = useState({ leads: [], pagination: {} });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [formData, setFormData] = useState({
    id: '', name: '', phone: '', email: '', passoutYear: '', hometown: '',
    followUpDate: '', department: '', course: '', channel: '', status: 'In-progress',
    college: '', remarks: '', message: ''
  });
  const [courses, setCourses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [formLoading, setFormLoading] = useState(false);
  const [nameError, setNameError] = useState('');
  const [isNameValid, setIsNameValid] = useState(true);
  const [phoneError, setPhoneError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [dateError, setDateError] = useState('');
  const [isDateValid, setIsDateValid] = useState(true);
  const [passoutYearError, setPassoutYearError] = useState('');
  const [isPassoutYearValid, setIsPassoutYearValid] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [followUpDateError, setFollowUpDateError] = useState('');
  const [isFollowUpDateValid, setIsFollowUpDateValid] = useState(true);
  const [location, setLocation] = useState(0);

  const channelOptions = [
    'Website', 'Phone', 'Email', 'Social Media', 'Data follow',
    'Webinar', 'Placement Drive', 'Corporates', 'Direct', 'Companies', 'Reference'
  ];

  const fetchLeads = async (tab = activeTab, pageNum = 1, perPage = rowsPerPage, search = searchTerm, location) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const status = search ? '' : (tab === 'All' ? '' : tab);
      const response = await makeApiRequest('post', '/api/getLeads', {
        token,
        page: pageNum,
        per_page: perPage,
        status,
        search,
        location
      });
      if (response && response.status === 'success') {
        setLeadsData(response.data);
        setTabCounts({
          All: response.data.pagination.total_all,
          New: response.data.pagination.total_new,
          'In-progress': response.data.pagination.total_in_progress,
          Deferred: response.data.pagination.total_deferred,
          Lost: response.data.pagination.total_lost,
          Converted: response.data.pagination.total_converted,
          Overdue: response.data.pagination.total_overdue,
        });
      } else {
        toast.error(response?.message || 'Failed to fetch leads');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Error fetching leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLeadsForExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await makeApiRequest('post', '/api/getAllLeads', {
        token,
        status: activeTab
      });
      if (response && response.status === 'success') {
        return response.data.leads;
      } else {
        toast.error(response?.message || 'Failed to fetch leads for export');
        return [];
      }
    } catch (error) {
      console.error('Export fetch error:', error);
      toast.error('Error fetching leads for export');
      return [];
    }
  };

  const handleExportExcel = async () => {
    const leads = await fetchAllLeadsForExport();
    if (leads.length === 0) return;
    const exportData = leads.map((lead, index) => ({
      'S.No': index + 1, Name: lead.name, Email: lead.email || '—', Phone: lead.phone || '—',
      'Follow-Up Date': formatDatewithMonth(lead.next_follow_up_date) || '—',
      'Course & Department': `${lead.course || '—'} / ${lead.department || '—'}`,
      'Passed-out Year': lead.pass_out_year || '—',
      'Created By & Date': `${lead.created_by || '—'} on ${formatDatewithMonth(lead.created_date) || '—'}`,
      Status: lead.status || '—'
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leads');
    XLSX.writeFile(wb, 'leads_export.xlsx');
  };

  const handleExportPDF = async () => {
    const leads = await fetchAllLeadsForExport();
    if (leads.length === 0) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    doc.setFontSize(16);
    doc.text('Leads Report', 14, 20);
    const tableColumn = [
      'S.No', 'Name', 'Email', 'Phone', 'Follow-Up Date',
      'Course & Dept', 'Year', 'Created By & Date', 'Status',
    ];
    const tableRows = leads.map((lead, index) => [
      index + 1, lead.name, lead.email || '—', lead.phone || '—',
      formatDatewithMonth(lead.next_follow_up_date) || '—',
      `${lead.course || '—'} / ${lead.department || '—'}`,
      lead.pass_out_year || '—',
      `${lead.created_by || '—'} on ${formatDatewithMonth(lead.created_date) || '—'}`,
      lead.status || '—',
    ]);
    doc.autoTable({
      head: [tableColumn], body: tableRows, startY: 30, margin: { left: 10, right: 10 },
      styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 8 }, 1: { cellWidth: 25 }, 2: { cellWidth: 30 }, 3: { cellWidth: 15 },
        4: { cellWidth: 20 }, 5: { cellWidth: 25 }, 6: { cellWidth: 12 }, 7: { cellWidth: 25 },
        8: { cellWidth: 15 },
      },
      theme: 'striped',
      headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0], fontSize: 8 },
      bodyStyles: { textColor: [0, 0, 0] },
    });
    doc.save('leads_export.pdf');
  };

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await makeApiRequest('post', '/api/course/list', { token });
      if (response && response.status === 'success') {
        setCourses(response.data);
      } else {
        console.error('Failed to fetch courses:', response?.message);
      }
    } catch (error) {
      console.error('API Error:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await makeApiRequest('post', '/api/departments', { token });
      if (response && response.status === 'success') {
        setDepartments(response.data);
      } else {
        console.error('Failed to fetch departments:', response?.message);
      }
    } catch (error) {
      console.error('API Error:', error);
    }
  };

  useEffect(() => {
    fetchLeads('New');
    fetchCourses();
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchLeads(activeTab, 1, rowsPerPage, searchTerm, location);
  }, [activeTab, rowsPerPage, searchTerm, location]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchLeads(activeTab, newPage + 1, rowsPerPage, searchTerm);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchLeads(activeTab, 1, newRowsPerPage, searchTerm);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const validateName = (name) => {
    if (!name.trim()) {
      setNameError('Name is required');
      setIsNameValid(false);
      return false;
    }
    setNameError('');
    setIsNameValid(true);
    return true;
  };

  const validatePhone = (phone) => {
    if (!phone) {
      setPhoneError('Phone number is required');
      setIsPhoneValid(false);
      return false;
    } else if (phone.length !== 10) {
      setPhoneError('Phone number must be 10 digits');
      setIsPhoneValid(false);
      return false;
    }
    setPhoneError('');
    setIsPhoneValid(true);
    return true;
  };

  const validateEmail = (email) => {
    if (email) {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      if (!emailRegex.test(email)) {
        setEmailError('Invalid email format (lowercase only)');
        setIsEmailValid(false);
        return false;
      }
      if (/[A-Z]/.test(email)) {
        setEmailError('Email must contain only lowercase letters');
        setIsEmailValid(false);
        return false;
      }
    }
    setEmailError('');
    setIsEmailValid(true);
    return true;
  };

  const validatePassoutYear = (year) => {
    if (year || year === 0) {
      const numericYear = String(year).replace(/[^0-9]/g, '');
      if (numericYear.length > 4) {
        setPassoutYearError('Pass-out year must not exceed 4 digits');
        setIsPassoutYearValid(false);
        return false;
      } else if (numericYear && (numericYear < 1900 || numericYear > new Date().getFullYear())) {
        setPassoutYearError(`Pass-out year must be between 1900 and ${new Date().getFullYear()}`);
        setIsPassoutYearValid(false);
        return false;
      }
    }
    setPassoutYearError('');
    setIsPassoutYearValid(true);
    return true;
  };

  const validateFollowUpDate = (date) => {
    if (!date) {
      setFollowUpDateError('Next Follow-Up Date is required');
      setIsFollowUpDateValid(false);
      return false;
    }
    setFollowUpDateError('');
    setIsFollowUpDateValid(true);
    return true;
  };

  const handleEditClick = (lead) => {
    const formattedLead = {
      id: lead.id, name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      passoutYear: lead.pass_out_year || '', hometown: lead.hometown || '',
      followUpDate: lead.next_follow_up_date || '', department: lead.department || '',
      course: lead.course || '', channel: lead.channel || '', status: lead.status || 'In-progress',
      remarks: lead.remarks || '', college: lead.college || '', message: ''
    };
    setSelectedLead(lead);
    setFormData(formattedLead);
    setIsEditModalOpen(true);
    validateName(formattedLead.name);
    validatePhone(formattedLead.phone);
    validateEmail(formattedLead.email);
    validatePassoutYear(formattedLead.passoutYear);
    validateFollowUpDate(formattedLead.followUpDate);
  };

  const handleViewClick = (lead) => {
    setSelectedLead(lead);
    setIsViewModalOpen(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };

    if (name === 'name') {
      validateName(value);
    } else if (name === 'phone') {
      const numericValue = value.replace(/[^0-9]/g, '');
      updatedFormData.phone = numericValue;
      validatePhone(numericValue);
    } else if (name === 'email') {
      validateEmail(value);
    } else if (name === 'passoutYear') {
      const numericValue = value.replace(/[^0-9]/g, '');
      updatedFormData.passoutYear = numericValue;
      validatePassoutYear(numericValue);
    } else if (name === 'followUpDate') {
      validateFollowUpDate(value);
    }
    setFormData(updatedFormData);
  };
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    const isNameValid = validateName(formData.name);
    const isPhoneValid = validatePhone(formData.phone);
    const isEmailValid = validateEmail(formData.email);
    const isPassoutYearValid = validatePassoutYear(formData.passoutYear);
    const isFollowUpDateValid = validateFollowUpDate(formData.followUpDate);

    if (!isNameValid || !isPhoneValid || !isEmailValid || !isPassoutYearValid || !isFollowUpDateValid) {
      toast.error('Please fix the errors before submitting');
      return;
    }
    if (!isDateValid) {
      toast.error('Follow-up date cannot be on a Saturday or Sunday');
      return;
    }
    setFormLoading(true);
    try {
      const apiData = {
        token: localStorage.getItem('authToken'), lead_id: formData.id, name: formData.name,
        phone: formData.phone, email: formData.email || null, pass_out_year: formData.passoutYear || null,
        hometown: formData.hometown || null, next_followup_date: formData.followUpDate || null,
        department_name: formData.department, course_name: formData.course, channel: formData.channel || null,
        status: formData.status, remarks: formData.remarks || null, college: formData.college || null,
        message: formData.message || null
      };
      const response = await makeApiRequest('post', '/api/updateLead', apiData);
      if (response && response.status === 'success') {
        toast.success('Lead updated successfully');
        setPage(0);
        setActiveTab('New');
        fetchLeads('New', 1, rowsPerPage, searchTerm);
        handleCloseEditModal();
      } else {
        toast.error(response?.message || 'Failed to update lead');
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedLead(null);
    setFormData({
      id: '', name: '', phone: '', email: '', passoutYear: '', hometown: '',
      followUpDate: '', department: '', course: '', channel: '', status: 'In-progress',
      college: '', remarks: '', message: ''
    });
    setNameError('');
    setIsNameValid(true);
    setPhoneError('');
    setIsPhoneValid(true);
    setDateError('');
    setIsDateValid(true);
    setPassoutYearError('');
    setIsPassoutYearValid(true);
    setEmailError('');
    setIsEmailValid(true);
    setFollowUpDateError('');
    setIsFollowUpDateValid(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedLead(null);
  };

  const [tabCounts, setTabCounts] = useState({
    All: 0, New: 0, 'In-progress': 0, Deferred: 0, Lost: 0, Converted: 0, Overdue: 0
  });

  return (
    <Box sx={{ padding: '5px', backgroundColor: '#F1F4F9', minHeight: '100vh' }}>
      <ToastContainer
        position="top-center" autoClose={2000} hideProgressBar={false} newestOnTop={false}
        closeOnClick={false} rtl={false} pauseOnFocusLoss draggable pauseOnHover={false} theme="light"
      />
      <Grid container sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Grid item xs={11.5}>
          <Box sx={{ marginTop: "0px", height: "80px", display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "#F1F4F9", marginBottom: "20px" }}>
            <Typography variant="h4" sx={{ color: "#481E82", fontWeight: "500", fontFamily: 'Roboto' }}>
              Follow-Up Lead
            </Typography>
            <Box>
              <Button
                variant="contained" onClick={handleExportPDF} startIcon={<PictureAsPdfIcon />}
                disabled={!leadsData.leads.length}
                sx={{ backgroundColor: "orange", width: { xs: "100%", sm: "fit-content" }, marginRight: { xs: 0, sm: 2 } }}
              >
                Download as PDF
              </Button>
              <Button
                variant="contained" onClick={handleExportExcel} startIcon={<GridOnIcon />}
                disabled={!leadsData.leads.length}
                sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45A049" }, width: { xs: "100%", sm: "fit-content" }, marginTop: { xs: 1, sm: 0 } }}
              >
                Export to EXCEL
              </Button>
            </Box>
          </Box>
          <Dialog open={isEditModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ bgcolor: "#f8f8f8", color: "#481E82", fontWeight: "500", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0" }}>
              Edit Lead
              <IconButton onClick={handleCloseEditModal} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required label="Name" name="name" fullWidth size="small" margin="normal"
                    value={formData.name} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Enter Name" error={!isNameValid} helperText={nameError}
                    sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required label="Phone" name="phone" fullWidth size="small" margin="normal"
                    value={formData.phone} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    error={!isPhoneValid} helperText={phoneError} placeholder="Enter Phone Number"
                    sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Email" name="email" fullWidth size="small" margin="normal"
                    value={formData.email} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Enter Email (lowercase only)" error={!isEmailValid} helperText={emailError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pass-out Year" name="passoutYear" fullWidth size="small" margin="normal"
                    value={formData.passoutYear} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Enter Pass-out Year" error={!isPassoutYearValid} helperText={passoutYearError}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Hometown" name="hometown" fullWidth size="small" margin="normal"
                    value={formData.hometown} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Enter Hometown"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    label="Next Follow-Up Date"
                    name="followUpDate"
                    type="date"
                    fullWidth
                    size='small'
                    margin="normal"
                    value={formData.followUpDate}
                    onChange={handleFormChange}
                    onFocus={(e) => e.target.showPicker()}
                    InputLabelProps={{ shrink: true }}
                    error={!isDateValid}
                    helperText={dateError}
                    sx={{
                      "& input": { color: formData.followUpDate ? "#000" : "#a2a2a2" },
                      "& input::-webkit-datetime-edit-text": { color: "#a2a2a2" },
                      "& input::-webkit-datetime-edit-year-field, & input::-webkit-datetime-edit-month-field, & input::-webkit-datetime-edit-day-field": {
                        color: formData.followUpDate ? "#000" : "#a2a2a2"
                      },
                      '& .MuiFormLabel-asterisk': { color: 'red' }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={formData.department}
                    onChange={(event, newValue) => setFormData({ ...formData, department: newValue || '' })}
                    options={departments.length === 0 ? ['Loading...'] : departments.map((dept) => dept.department_name)}
                    disableClearable
                    renderInput={(params) => (
                      <TextField {...params} label="Department" placeholder="Select Department" fullWidth size="small" margin="normal" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={courses.find((c) => c.course_name === formData.course) || null}
                    onChange={(event, newValue) => setFormData({ ...formData, course: newValue ? newValue.course_name : '' })}
                    options={courses}
                    getOptionLabel={(option) => option.course_name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderInput={(params) => (
                      <TextField {...params} label="Course" placeholder="Select Course" fullWidth size="small" margin="normal" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    value={formData.channel}
                    onChange={(event, newValue) => setFormData({ ...formData, channel: newValue || '' })}
                    options={channelOptions}
                    disableClearable
                    renderInput={(params) => (
                      <TextField {...params} label="Channel" placeholder="Select Channel" fullWidth size="small" margin="normal" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Status" name="status" select fullWidth size="small" margin="normal"
                    value={formData.status} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Select Status"
                  >
                    <MenuItem value="" disabled>Select Status</MenuItem>
                    <MenuItem value="New">New</MenuItem>
                    <MenuItem value="In-progress">In-progress</MenuItem>
                    <MenuItem value="Deferred">Later</MenuItem>
                    <MenuItem value="Lost">Lost</MenuItem>
                    <MenuItem value="Converted">Converted</MenuItem>
                    <MenuItem value="Overdue">Overdue</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="College" name="college" fullWidth size="small" margin="normal"
                    value={formData.college} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                    placeholder="Enter College Name"
                  />
                </Grid>
                {formData.status !== 'Lost' && formData.status !== 'Converted' && (
                  <Grid item xs={12}>
                    <TextField
                      label="Follow-Up Remark" name="message" multiline rows={3} fullWidth margin="normal"
                      value={formData.message} onChange={handleFormChange} InputLabelProps={{ shrink: true }}
                      placeholder="Enter Follow-Up Remark"
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
              <Button
                variant="contained" sx={{ backgroundColor: "rgb(249, 90, 90)", color: '#fff', '&:hover': { backgroundColor: "rgb(220, 70, 70)" } }}
                onClick={handleCloseEditModal} disabled={formLoading}
              >
                CANCEL
             </Button>
              <Button
                variant="contained" color="primary" onClick={handleUpdateSubmit}
                disabled={formLoading || !isNameValid || !isPhoneValid || !isEmailValid || !isPassoutYearValid || !isFollowUpDateValid}
              >
                {formLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                    UPDATING...
                  </>
                ) : 'UPDATE'}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog open={isViewModalOpen} onClose={handleCloseViewModal} maxWidth="lg" fullWidth>
            <ViewLead lead={selectedLead} onClose={handleCloseViewModal} />
          </Dialog>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'end' }}>
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex', justifyContent: "end" }}>
              <TextField
                value={searchTerm} onChange={handleSearchChange} size="small" placeholder="Search" sx={{ backgroundColor: 'white' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  sx: { '& input': { paddingLeft: '4px' } },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3} sx={{ display: 'flex', justifyContent: "center" }}>
              <TextField
                size="small"
                label="Location"
                variant="outlined"
                select
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                sx={{ fontFamily: 'sans-serif', backgroundColor: 'white', width: '80%' }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="0">All Location</MenuItem>
                <MenuItem value="1">Chennai</MenuItem>
                <MenuItem value="2">Trichy</MenuItem>
              </TextField>
            </Grid>
          </Box>
          <Paper elevation={0} sx={{ mb: 2, borderRadius: "5px", overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', borderBottom: '1px solid #e0e0e0', alignItems: 'center', px: 0, py: 2.5 }}>
              <Box sx={{ display: 'flex', flexGrow: 1, overflowX: 'auto' }}>
                <TabButton startIcon={<PeopleOutlineIcon />} active={activeTab === 'All'} onClick={() => handleTabChange('All')}>
                  ALL ({tabCounts.All})
                </TabButton>
                <TabButton startIcon={<PersonAddIcon />} active={activeTab === 'New'} onClick={() => handleTabChange('New')}>
                  NEW ({tabCounts.New})
                </TabButton>
                <TabButton startIcon={<AutorenewIcon />} active={activeTab === 'In-progress'} onClick={() => handleTabChange('In-progress')}>
                  IN PROGRESS ({tabCounts['In-progress']})
                </TabButton>
                <TabButton startIcon={<AccessTimeIcon />} active={activeTab === 'Deferred'} onClick={() => handleTabChange('Deferred')}>
                  LATER ({tabCounts.Deferred})
                </TabButton>
                <TabButton startIcon={<HighlightOffIcon />} active={activeTab === 'Lost'} onClick={() => handleTabChange('Lost')}>
                  LOST ({tabCounts.Lost})
                </TabButton>
                <TabButton startIcon={<CheckCircleOutlineIcon />} active={activeTab === 'Converted'} onClick={() => handleTabChange('Converted')}>
                  CONVERTED ({tabCounts.Converted})
                </TabButton>
                <TabButton startIcon={<HourglassEmptyIcon />} active={activeTab === 'Overdue'} onClick={() => handleTabChange('Overdue')}>
                  OVERDUE ({tabCounts.Overdue})
                </TabButton>
              </Box>
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={40} thickness={4} sx={{ color: '#481E82' }} />
              </Box>
            ) : (
              <>
                <TableContainer
                  sx={{
                    maxWidth: '100%', height: '60vh', overflowY: 'auto', overflowX: 'auto',
                    '&::-webkit-scrollbar': { height: '8px', width: '8px' },
                    '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px' }
                  }}
                >
                  <Table sx={{ width: 1688, tableLayout: 'fixed' }} aria-label="leads table">
                    <TableHead sx={{ backgroundColor: '#ECEDFF', height: "64px" }}>
                      <TableRow>
                        <StyledTableHeaderCell width={columnWidths.sno}><strong>S.NO</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.name}><strong>Name</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.email}><strong>Email</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.phone} sx={{ textAlign: 'center' }}><strong>Phone</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.date}><strong>Follow-Up Date</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.courseDepartment}><strong>Course & Department</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.year}><strong>Passed-out Year</strong></StyledTableHeaderCell>
                        <StyledTableHeaderCell width={columnWidths.createdByDate}><strong>Created By & Date</strong></StyledTableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leadsData.leads.length > 0 ? (
                        leadsData.leads.map((lead, index) => (
                          <StyledTableRow key={lead.id}>
                            <StyledTableCell sx={{ width: columnWidths.sno }}>
                              <span>{(page * rowsPerPage) + index + 1}</span>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.name }}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton size="small" sx={{ color: '#1976d2', padding: '4px', mr: 1 }} onClick={() => handleViewClick(lead)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" sx={{ color: '#1976d2', padding: '4px', mr: 1 }} onClick={() => handleEditClick(lead)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <Tooltip title={lead.name} placement="top" sx={{ fontSize: '2rem', padding: '16px 24px', backgroundColor: 'rgba(0, 0, 0, 0.9)', maxWidth: '800px' }}>
                                  <span>{lead.name}</span>
                                </Tooltip>
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.email }}>
                              {lead.email ? (
                                <Tooltip title={lead.email} placement="top" sx={{ fontSize: '2rem', padding: '16px 24px', backgroundColor: 'rgba(0, 0, 0, 0.9)', maxWidth: '800px' }}>
                                  <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${lead.email}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>
                                    {lead.email}
                                  </a>
                                </Tooltip>
                              ) : '—'}
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.phone, textAlign: 'center', color: 'green' }}>
                              <span>
                                {lead.phone && lead.phone.length === 10
                                  ? `${lead.phone.slice(0, 5)} ${lead.phone.slice(5)}`
                                  : lead.phone || '—'}
                              </span>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.date, color: 'purple' }}>
                              <span>{formatDatewithMonth(lead.next_follow_up_date) || '-'}</span>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.courseDepartment }}>
                              <Tooltip title={`${lead.department || '—'} / ${lead.course || '—'}`} placement="top" sx={{ fontSize: '2rem', padding: '16px 24px', backgroundColor: 'rgba(0, 0, 0, 0.9)', maxWidth: '800px' }}>
                                <span>
                                  {!lead.department && !lead.course ? (
                                    '—'
                                  ) : (
                                    <>
                                      {lead.department || '—'}
                                      <br />
                                      <span style={{ color: '#757575' }}>
                                        {lead.course || '—'}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </Tooltip>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.year }}>
                              <span>{lead.pass_out_year || '—'}</span>
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: columnWidths.createdByDate }}>
                              <Tooltip title={`${lead.created_by || '—'} on ${formatDatewithMonth(lead.created_date) || '—'}`} placement="top" sx={{ fontSize: '2rem', padding: '16px 24px', backgroundColor: 'rgba(0, 0, 0, 0.9)', maxWidth: '800px' }}>
                                <span>
                                  {lead.created_by || '—'}
                                  <br />
                                  <span style={{ color: '#757575', fontSize: '12px' }}>
                                    on {formatDatewithMonth(lead.created_date) || '—'}
                                  </span>
                                </span>
                              </Tooltip>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))
                      ) : (
                        <StyledTableRow>
                          <StyledTableCell colSpan={8} sx={{ py: 4, textAlign: 'center' }}>
                            {searchTerm
                              ? `No leads found for "${searchTerm}" across all statuses`
                              : 'No leads found'}
                          </StyledTableCell>
                        </StyledTableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]} component="div" count={leadsData.pagination.total || 0}
                  rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FollowUpLead;